import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import HelpOutline from '@mui/icons-material/HelpOutline'
import {
  Card,
  Container,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import useProjectFeatureEnabled from '@/hooks/useProjectFeatureEnabled'
import {
  useCreateCustomVariableValue,
  useGetCustomVariableValues,
  useUpdateCustomVariableValue,
} from '@/service-library/hooks/custom-variable-values'
import { useGetCustomVariables } from '@/service-library/hooks/custom-variables'
import useAllOrganizations from '@/services/hooks/useAllOrganizations'
import { CustomVariable, CustomVariableValue } from '@/types/custom-variables'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import LargeHeading from '@/components/large-heading/LargeHeading'
import PageTitle from '@/components/PageTitle'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import DataProviderOrgPOCard from './DataProviderOrgPOCard'
import POInstructionsField from './POInstructionsField'
import POMatchHelpDialog from './POMatchHelpDialog'
import PONumbersMustMatchSwitch from './PONumbersMustMatchSwitch'

const PURCHASE_ORDERS_CATEGORY_ID = '01958d9a-b5b2-7d6e-8a31-7d2a5b8a9001'

export default function PurchaseOrderMatchingPage() {
  const { project } = useProjectContext()
  const helpOverlay = useOverlay()

  const navigate = useNavigate()
  const { isEnabled: isPurchaseOrdersEnabled, isPending } =
    useProjectFeatureEnabled('purchase-orders')

  useEffect(() => {
    if (!isPending && !isPurchaseOrdersEnabled) {
      navigate(`/projects/${project.id}/settings/general`, { replace: true })
    }
  }, [isPending, isPurchaseOrdersEnabled, navigate, project.id])

  const { customVariables, isPending: isPendingVariables } =
    useGetCustomVariables({
      filters: {
        category_id: PURCHASE_ORDERS_CATEGORY_ID,
        limit: '100',
      },
    })

  const {
    customVariableValues,
    queryKey: customVariableValuesQueryKey,
    isPending: isPendingValues,
  } = useGetCustomVariableValues({
    filters: {
      project_id: project.id,
      custom_variable_id__in: customVariables.map((v) => v.id).join(','),
      limit: '10000', // TODO: implement pagination if there are a lot of variables/values
    },
    enabled: customVariables.length > 0,
  })

  const { organizations: dataProviderOrgs, isFetchingAll: isLoadingOrgs } =
    useAllOrganizations({
      filters: {
        is_relay_data_provider: 'true',
      },
    })

  const onError = () =>
    showErrorSnackbar('Failed to save. Please try again later.')

  const { createCustomVariableValue } = useCreateCustomVariableValue({
    listQueryKey: customVariableValuesQueryKey,
    onError,
  })

  const { updateCustomVariableValue } = useUpdateCustomVariableValue({
    listQueryKey: customVariableValuesQueryKey,
    onError,
  })

  const handleOnValueChange = useCallback(
    (
      variableValue: Omit<CustomVariableValue, 'id' | 'project_id'> & {
        id?: string
      },
    ) => {
      if (variableValue.id) {
        updateCustomVariableValue({
          ...variableValue,
          project_id: project.id,
          id: variableValue.id,
        })
      } else {
        createCustomVariableValue({
          ...variableValue,
          project_id: project.id,
          id: generateUuid(),
        })
      }
    },
    [createCustomVariableValue, project.id, updateCustomVariableValue],
  )

  const variablesByName = useMemo(() => {
    const map: Record<string, CustomVariable | undefined> = {}
    customVariables.forEach((v) => {
      map[v.name] = v
    })
    return map
  }, [customVariables])

  const performMatchVar = variablesByName[
    'z-per_company_perform_po_match'
  ] as CustomVariable
  const globalInstructionsVar = variablesByName[
    'z-global_po_matching_additional_instructions'
  ] as CustomVariable
  const perCompanyInstructionsVar = variablesByName[
    'z-per_company_po_matching_additional_instructions'
  ] as CustomVariable
  const excludedVendorsVar = variablesByName[
    'z-per_company_excluded_vendor_numbers'
  ] as CustomVariable
  const poNumbersMustMatchVar = variablesByName[
    'z-global_po_numbers_must_match'
  ] as CustomVariable

  const isLoading = isPendingVariables || isPendingValues || isLoadingOrgs

  const valuesByKey = useMemo(() => {
    const map = new Map<string, CustomVariableValue>()
    customVariableValues.forEach((v) => {
      map.set(`${v.custom_variable_id}:${v.owner_org_id}`, v)
    })
    return map
  }, [customVariableValues])

  const findValue = (variableId: string, ownerOrgId: string) => {
    if (!variableId || !ownerOrgId) return undefined
    return valuesByKey.get(`${variableId}:${ownerOrgId}`)
  }

  const globalInstructionsValue = findValue(
    globalInstructionsVar?.id || '',
    project.org_id,
  )
  const poNumbersMustMatchValue = findValue(
    poNumbersMustMatchVar?.id || '',
    project.org_id,
  )

  return (
    <Container maxWidth="md" sx={{ height: '100%' }}>
      <PageTitle>Purchase Order Matching</PageTitle>
      <ProjectBreadcrumb
        label="Purchase Order Matchings"
        url={`/projects/${project.id}/settings/purchase-orders`}
      />
      <Stack spacing={3} sx={{ pb: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <LargeHeading heading="Purchase Order Matching" />
          <Tooltip title="What is Purchase Order Matching?">
            <IconButton
              onClick={helpOverlay.open}
              size="small"
              aria-label="What is Purchase Order Matching?"
            >
              <HelpOutline />
            </IconButton>
          </Tooltip>
        </Stack>
        <POMatchHelpDialog overlay={helpOverlay} />

        {isLoading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={100} />
            <Skeleton variant="rounded" height={180} />
            <Skeleton variant="rounded" height={120} />
          </Stack>
        ) : (
          <>
            <Card
              variant="outlined"
              component={Stack}
              spacing={2}
              sx={{ p: 2 }}
            >
              <Typography variant="subtitle1">
                Purchase Order Match Rules
              </Typography>
              <PONumbersMustMatchSwitch
                variable={poNumbersMustMatchVar}
                value={poNumbersMustMatchValue}
                ownerOrgId={project.org_id}
                onValueChange={handleOnValueChange}
              />
            </Card>
            <Card
              variant="outlined"
              component={Stack}
              spacing={2}
              sx={{ p: 2 }}
            >
              <Typography variant="subtitle1">
                Purchase Order Match Instructions
              </Typography>
              <POInstructionsField
                label=""
                value={globalInstructionsValue?.value ?? ''}
                onChange={(instructions) => {
                  handleOnValueChange({
                    ...globalInstructionsValue,
                    custom_variable_id: globalInstructionsVar.id,
                    owner_org_id: project.org_id,
                    value: instructions,
                  })
                }}
                helperText={globalInstructionsVar.description ?? ''}
              />
            </Card>

            <Stack spacing={2}>
              <Typography variant="h6">Companies</Typography>
              {dataProviderOrgs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No relay data provider organizations found.
                </Typography>
              ) : (
                dataProviderOrgs.map((org) => (
                  <DataProviderOrgPOCard
                    key={org.id}
                    dataProviderOrg={org}
                    performMatchVariable={performMatchVar}
                    instructionsVariable={perCompanyInstructionsVar}
                    excludedVendorsVariable={excludedVendorsVar}
                    orgCredentialTypeId={project.org_credential_type_id ?? ''}
                    performMatchValue={findValue(performMatchVar.id, org.id)}
                    instructionsValue={findValue(
                      perCompanyInstructionsVar.id,
                      org.id,
                    )}
                    excludedVendorsValue={findValue(
                      excludedVendorsVar.id,
                      org.id,
                    )}
                    onValueChange={handleOnValueChange}
                  />
                ))
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Container>
  )
}
